import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Input } from "reactstrap";
import { Link } from "react-router-dom";
import company from "../../assets/images/auvenir-logo-dark.svg";
import piersLogo from "../../assets/images/landing/piers-logo-light.png";
import loginHeroBackground from "../../assets/images/login-otp-background.png";
import { postOTPRequest, resendOTPRequest } from "../../helpers/backend_helper";
import { loadAuthenticatedUser } from "../../helpers/auth-session";
import { showLoading } from "react-global-loading";
import "./Login.scss";
import "./TwoStepVerfication.scss";

const TwoStepVerfication = () => {
  const [otpError, setOTPError] = useState("");
  const [resending, setResending] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(120);
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [errors, setErrors] = useState({});
  const otpInputs = useRef([]);
  const authCode = otpDigits.join("");

  useEffect(() => {
    otpInputs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const timer = window.setInterval(
      () => setResendSeconds((seconds) => Math.max(0, seconds - 1)),
      1000
    );
    return () => window.clearInterval(timer);
  }, [resendSeconds]);

  const updateOtpDigits = (nextDigits) => {
    setOtpDigits(nextDigits);
    if (nextDigits.some(Boolean)) setErrors({});
    setOTPError("");
  };

  const handleOtpChange = (index, value) => {
    const digits = value.replace(/\D/g, "").slice(0, 6 - index);
    const nextDigits = [...otpDigits];
    if (!digits) nextDigits[index] = "";
    else
      digits.split("").forEach((digit, offset) => {
        nextDigits[index + offset] = digit;
      });
    updateOtpDigits(nextDigits);
    const nextIndex = Math.min(index + Math.max(digits.length, 1), 5);
    if (digits)
      requestAnimationFrame(() => otpInputs.current[nextIndex]?.focus());
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (event) => {
    event.preventDefault();
    const digits = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6)
      .split("");
    if (!digits.length) return;
    const nextDigits = ["", "", "", "", "", ""];
    digits.forEach((digit, index) => {
      nextDigits[index] = digit;
    });
    updateOtpDigits(nextDigits);
    requestAnimationFrame(() =>
      otpInputs.current[Math.min(digits.length, 6) - 1]?.focus()
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!authCode || authCode.length !== 6) {
      setErrors({ otpcode: "Enter the 6-digit verification code." });
      return;
    }

    showLoading(true);
    postOTPRequest({ otpCode: authCode })
      .then(async (response) => {
        if (response.status === true) {
          try {
            await loadAuthenticatedUser();
            // Choose the landing page from the saved onboarding preference.
            window.location.replace("/piers/landing");
          } catch {
            setOTPError(
              "Verification succeeded, but we could not establish your session. Please sign in again."
            );
          }
        } else setOTPError(response.message);
      })
      .catch(() =>
        setOTPError("We could not verify your code. Please try again.")
      )
      .finally(() => showLoading(false));
  };

  const handleResend = async (event) => {
    event.preventDefault();
    if (resending) return;
    setResending(true);
    setOTPError("");
    try {
      const response = await resendOTPRequest();
      if (!response?.status)
        throw new Error(response?.message || "Unable to resend the code.");
      setOtpDigits(["", "", "", "", "", ""]);
      setResendSeconds(120);
      requestAnimationFrame(() => otpInputs.current[0]?.focus());
    } catch (error) {
      setOTPError(error.message || "Unable to resend the code.");
    } finally {
      setResending(false);
    }
  };

  return (
    <main
      className="login-design otp-design"
      style={{ backgroundImage: `url(${loginHeroBackground})` }}
    >
      <section
        className="login-design__brand"
        aria-label="PIERS inspection platform"
      >
        <div className="login-design__brand-content">
          <img
            src={company}
            alt="Streamworks Tech"
            className="login-design__company-logo"
          />
          <div className="login-design__brand-copy">
            <img
              src={piersLogo}
              alt="PIERS — Practice Inspection & Engagement Review System"
              className="login-design__piers-logo"
            />
          </div>
          <div className="login-design__brand-values">
            <div>
              <i className="bx bx-search-alt-2" />
              <strong>Inspect</strong>
              <small>
                Plan and perform
                <br />
                engagement inspections
              </small>
            </div>
            <div>
              <i className="bx bx-task" />
              <strong>Review</strong>
              <small>
                Evaluate findings
                <br />
                with clarity and consistency
              </small>
            </div>
            <div>
              <i className="bx bx-shield-quarter" />
              <strong>Assure</strong>
              <small>Mitigate risk and maintain quality standards</small>
            </div>
            <div>
              <i className="bx bx-line-chart" />
              <strong>Improve</strong>
              <small>Drive insights and continuous improvement</small>
            </div>
          </div>
        </div>
      </section>

      <section className="login-design__sign-in">
        <div className="login-design__card otp-design__card">
          <div className="login-design__security-mark">
            <i className="bx bx-lock-alt" />
          </div>
          <header>
            <h1>Verify your OTP</h1>
            <p>
              Enter the 6-digit verification code sent to your registered email.
            </p>
            <Link to="/login" className="otp-design__back">
              <i className="bx bx-left-arrow-alt" /> Back to sign in
            </Link>
          </header>
          {otpError && (
            <Alert color="danger" className="login-design__error">
              {otpError}
            </Alert>
          )}
          <Form onSubmit={handleSubmit} noValidate>
            <Input type="hidden" name="otpcode" value={authCode} />
            <div className="otp-design__code">
              {otpDigits.map((digit, index) => (
                <Input
                  key={index}
                  innerRef={(element) => {
                    otpInputs.current[index] = element;
                  }}
                  aria-label={`Verification code digit ${index + 1}`}
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                  className="form-control form-control-lg text-center"
                  inputMode="numeric"
                  maxLength={6}
                  onChange={(event) =>
                    handleOtpChange(index, event.target.value)
                  }
                  onKeyDown={(event) => handleOtpKeyDown(index, event)}
                  onPaste={handleOtpPaste}
                  pattern="[0-9]*"
                  style={{
                    padding: "8px",
                    borderRadius: "10px",
                    fontSize: "21px",
                    fontWeight: "700",
                    textAlign: "center",
                    border: "1px solid #d6dfee",
                    color: "#143c9a",
                  }}
                  type="text"
                  value={digit}
                />
              ))}
            </div>
            {errors.otpcode && (
              <p className="otp-design__error">{errors.otpcode}</p>
            )}
            <Button
              className="login-design__submit"
              color="primary"
              type="submit"
            >
              Verify code
              <i className="bx bx-right-arrow-alt" />
            </Button>
          </Form>
          <div className="otp-design__resend">
            Didn&apos;t receive an OTP?{" "}
            {resendSeconds > 0 ? (
              <span>
                Resend code in{" "}
                {String(Math.floor(resendSeconds / 60)).padStart(2, "0")}:
                {String(resendSeconds % 60).padStart(2, "0")}
              </span>
            ) : (
              <Link to="#" onClick={handleResend}>
                {resending ? "Sending…" : "Resend code"}
              </Link>
            )}
          </div>
          <footer className="login-design__trust">
            <div>
              <i className="bx bx-shield-quarter" />
              <span>
                <b>Secure &amp; Trusted</b>
                <small>Enterprise grade security</small>
              </span>
            </div>
            <div>
              <i className="bx bx-lock-alt" />
              <span>
                <b>Data Protected</b>
                <small>Your data is safe with us</small>
              </span>
            </div>
            <div>
              <i className="bx bx-check-shield" />
              <span>
                <b>Always Available</b>
                <small>Reliable platform you can count on</small>
              </span>
            </div>
          </footer>
          <p className="login-design__copyright">
            © {new Date().getFullYear()} Streamworks Tech. All rights reserved.
            <br />v 1.0.0.1
          </p>
        </div>
      </section>
    </main>
  );
};

export default TwoStepVerfication;
